import { useMemo, useState } from "react";
import styled, { ThemeProvider } from "styled-components";
import { AnimatePresence, motion } from "motion/react";
import { token } from "../../../../theme";
import Tabs from "../../../../components/Tabs/Tabs";
import MSButton from "../../../../components/MSButton/MSButton";
import computerIcon from "../../../../assets/images/computer_explorer-3.png";
import gearsIcon from "../../../../assets/images/gears_tweakui_a-1.png";
import envelopeIcon from "../../../../assets/images/envelope_closed-0.png";
import { PROFILE } from "../../../../config/profile";
import { useDesktop } from "../../utils/DesktopContext";
import { getProjectStats, getTechUsage } from "../../utils/projectStats";
import { getProjectIcon, useOpenProject } from "../../utils/useOpenProject";

interface SystemPropertiesProps {
  onClose?: () => void;
}

const LINK_LABELS: Record<keyof typeof PROFILE.links, string> = {
  email: "E-mail",
  github: "GitHub",
  linkedin: "LinkedIn",
  website: "Website",
};

const toHref = (key: string, value: string) =>
  key === "email" && !value.startsWith("mailto:") ? `mailto:${value}` : value;

const SystemProperties = ({ onClose }: SystemPropertiesProps) => {
  const { projects, loadStatus } = useDesktop();
  const { openProject } = useOpenProject();
  const stats = useMemo(() => getProjectStats(projects), [projects]);
  const techUsage = useMemo(() => getTechUsage(projects), [projects]);
  const [expanded, setExpanded] = useState<string[]>([]);
  const maxUsage = techUsage[0]?.projects.length || 1;

  const contactLinks = Object.entries(PROFILE.links).filter(
    ([, value]) => value
  ) as [keyof typeof PROFILE.links, string][];

  const toggle = (name: string) =>
    setExpanded((prev) =>
      prev.includes(name) ? prev.filter((x) => x !== name) : [...prev, name]
    );

  const general = (
    <Panel>
      <GeneralLayout>
        <Monitor>
          <img src={computerIcon} alt="" />
        </Monitor>
        <Specs>
          <SpecGroup>
            <SpecLabel>System:</SpecLabel>
            <div>{PROFILE.osName}</div>
            <div>Portfolio Edition</div>
            <div>4.20.1998 A</div>
          </SpecGroup>
          <SpecGroup>
            <SpecLabel>Registered to:</SpecLabel>
            <div>{PROFILE.name}</div>
            <div>{PROFILE.tagline}</div>
          </SpecGroup>
          <SpecGroup>
            <SpecLabel>Computer:</SpecLabel>
            <div>Creativity(tm) Processor</div>
            {loadStatus === "ready" ? (
              <>
                <div>{stats.total} projects installed</div>
                <div>{stats.featured} featured on the desktop</div>
                <div>{stats.techCount} technologies loaded</div>
                {stats.firstYear && (
                  <div>
                    Shipping since {stats.firstYear}
                    {stats.latestYear && stats.latestYear !== stats.firstYear
                      ? ` (latest: ${stats.latestYear})`
                      : ""}
                  </div>
                )}
              </>
            ) : (
              <div>
                {loadStatus === "loading"
                  ? "Detecting hardware..."
                  : "Project drive not responding"}
              </div>
            )}
          </SpecGroup>
        </Specs>
      </GeneralLayout>
    </Panel>
  );

  const deviceManager = (
    <Panel>
      <Hint>
        Technologies used across my projects, most used first. Expand one to see
        where it's used, double-click a project to open it.
      </Hint>
      <Tree>
        <TreeRow>
          <img src={computerIcon} alt="" />
          <b>{PROFILE.name}'s Computer</b>
        </TreeRow>
        {techUsage.length === 0 && (
          <TreeChildren>
            <TreeRow>No devices detected yet.</TreeRow>
          </TreeChildren>
        )}
        <TreeChildren>
          {techUsage.map((tech) => {
            const isOpen = expanded.includes(tech.name);
            return (
              <li key={tech.name}>
                <TreeRow as="button" onClick={() => toggle(tech.name)}>
                  <Expander>{isOpen ? "−" : "+"}</Expander>
                  <img src={gearsIcon} alt="" />
                  <span>{tech.name}</span>
                  <UsageBar
                    style={{
                      width: `${(tech.projects.length / maxUsage) * 60}px`,
                    }}
                  />
                  <Count>{tech.projects.length}</Count>
                </TreeRow>
                <AnimatePresence initial={false}>
                  {isOpen && (
                    <TreeChildren
                      as={motion.ul}
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.15 }}
                      style={{ overflow: "hidden" }}
                    >
                      {tech.projects.map((project) => (
                        <li key={project.id}>
                          <TreeRow
                            as="button"
                            onDoubleClick={() => openProject(project)}
                            title="Double-click to open"
                          >
                            <img src={getProjectIcon(project)} alt="" />
                            <span>{project.title}</span>
                          </TreeRow>
                        </li>
                      ))}
                    </TreeChildren>
                  )}
                </AnimatePresence>
              </li>
            );
          })}
        </TreeChildren>
      </Tree>
    </Panel>
  );

  const contact = (
    <Panel>
      <GeneralLayout>
        <Monitor>
          <img src={envelopeIcon} alt="" />
        </Monitor>
        <Specs>
          <SpecGroup>
            <SpecLabel>Get in touch:</SpecLabel>
            <div>Always happy to chat about new projects and ideas.</div>
          </SpecGroup>
          {contactLinks.map(([key, value]) => (
            <SpecGroup key={key}>
              <SpecLabel>{LINK_LABELS[key]}:</SpecLabel>
              <a
                href={toHref(key, value)}
                target="_blank"
                rel="noopener noreferrer"
              >
                {value.replace(/^mailto:|^https?:\/\/(www\.)?/, "")}
              </a>
            </SpecGroup>
          ))}
        </Specs>
      </GeneralLayout>
    </Panel>
  );

  const tabs = [
    { title: "General", content: general },
    { title: "Device Manager", content: deviceManager },
    ...(contactLinks.length ? [{ title: "Contact", content: contact }] : []),
  ];

  return (
    <ThemeProvider theme={token}>
      <Container>
        <Tabs items={tabs} />
        {onClose && (
          <Buttons>
            <MSButton onClick={onClose}>OK</MSButton>
          </Buttons>
        )}
      </Container>
    </ThemeProvider>
  );
};

export default SystemProperties;

const Container = styled.div`
  width: 440px;
  max-width: calc(100vw - 16px);
  font-size: ${(p) => p.theme.fontNormal}px;
`;

const Panel = styled.div`
  height: 320px;
  overflow-y: auto;
  padding: 16px;
`;

const GeneralLayout = styled.div`
  display: flex;
  gap: 24px;
`;

const Monitor = styled.div`
  flex-shrink: 0;
  width: 110px;
  height: 90px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #008080;
  border: 6px solid #c0c0c0;
  box-shadow:
    ${(p) => p.theme.standardBoxShadow},
    0 0 0 1px #000;

  & img {
    width: 48px;
    height: 48px;
  }
`;

const Specs = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  line-height: 1.5;

  & a {
    color: #00007b;
  }
`;

const SpecGroup = styled.div`
  & > div:not(:first-child),
  & > a {
    padding-left: 16px;
    display: block;
  }
`;

const SpecLabel = styled.div`
  font-weight: 700;
`;

const Hint = styled.div`
  margin-bottom: 10px;
  line-height: 1.4;
`;

const Tree = styled.div`
  background: #fff;
  box-shadow: ${(p) => p.theme.invertedStandardBoxShadow};
  padding: 6px;
  min-height: 220px;
`;

const TreeChildren = styled.ul`
  list-style: none;
  margin: 0 0 0 10px;
  padding-left: 10px;
  border-left: 1px dotted #808080;
`;

const TreeRow = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
  width: 100%;
  padding: 2px;
  background: transparent;
  border: none;
  font-size: ${(p) => p.theme.fontNormal}px;
  text-align: left;
  cursor: default;
  color: #000;

  & img {
    width: 16px;
    height: 16px;
  }

  &:is(button) {
    cursor: pointer;
  }

  &:is(button):focus span {
    background: #00007b;
    color: #fff;
    outline: 1px dotted #ffff00;
  }
`;

const Expander = styled.i`
  font-style: normal;
  width: 9px;
  height: 9px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 9px;
  line-height: 1;
  border: 1px solid #808080;
  background: #fff;
  flex-shrink: 0;
`;

const UsageBar = styled.i`
  font-style: normal;
  display: inline-block;
  height: 6px;
  margin-left: auto;
  background: linear-gradient(90deg, #00007b, #1085d2);
`;

const Count = styled.i`
  font-style: normal;
  min-width: 18px;
  text-align: right;
  color: #404040;
`;

const Buttons = styled.div`
  display: flex;
  justify-content: flex-end;
  padding: 0 1rem 1rem;
`;
